export const REFERENCE_REVIEW_SOURCE_TYPE = 'reference_review';
export const REFERENCE_REVIEW_CRITERION_NAME = 'Revue selon un référentiel';

const SNAPSHOT_METADATA_KEYS = [
    'analysis_id',
    'contract_document_version_id',
    'document_version_id',
    'criterion_id',
    'criterion_name',
    'criterion_title',
    'review_id',
    'reference_review_id',
    'snapshot_id',
    'stream_instance_id',
    'reference_document_id',
    'reference_document_title',
    'reference_file_id',
    'reference_file_title',
    'reference_content_hash',
    'objective',
    'pov',
    'studied_party',
    'completion_status',
    'coverage_complete',
    'snapshot_persisted',
    'created_at',
    'finalized_at',
];

const asObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const firstPresent = (...values) => values.find((value) => (
    value !== undefined && value !== null && String(value).trim() !== ''
));

const uniqueIds = (items = []) => {
    const seen = new Set();
    return items.reduce((order, item) => {
        const id = String(item?.requirement_id || '').trim();
        if (!id || seen.has(id)) return order;
        seen.add(id);
        order.push(id);
        return order;
    }, []);
};

/**
 * Accepts both the historical raw terminal payload and the richer read-endpoint
 * envelope. It returns a new object and never mutates the persisted snapshot.
 */
export function normalizeReferenceReviewSnapshotPayload(payload) {
    const envelope = asObject(payload);
    const nestedEnvelope = asObject(
        envelope.snapshot
        || envelope.snapshot_json
        || envelope.result
    );
    const nestedAnalytical = asObject(
        nestedEnvelope.snapshot
        || nestedEnvelope.snapshot_json
        || nestedEnvelope.result
    );
    const analytical = Object.keys(nestedAnalytical).length > 0
        ? nestedAnalytical
        : Object.keys(nestedEnvelope).length > 0
            ? nestedEnvelope
            : envelope;
    const metadata = {
        ...asObject(analytical.reference_review_metadata),
        ...asObject(analytical.snapshot_metadata),
        ...asObject(nestedEnvelope.reference_review_metadata),
        ...asObject(nestedEnvelope.snapshot_metadata),
        ...asObject(envelope.reference_review_metadata),
        ...asObject(envelope.snapshot_metadata),
        ...asObject(envelope.metadata),
    };
    const normalized = {
        ...analytical,
        snapshot_metadata: metadata,
        requirements: Array.isArray(analytical.requirements)
            ? [...analytical.requirements]
            : [],
        verdicts: Array.isArray(analytical.verdicts)
            ? [...analytical.verdicts]
            : [],
    };

    SNAPSHOT_METADATA_KEYS.forEach((key) => {
        const value = firstPresent(envelope[key], nestedEnvelope[key], metadata[key], analytical[key]);
        if (value !== undefined) normalized[key] = value;
    });
    return normalized;
}

export function getReferenceReviewScope(snapshotOrContext = {}) {
    const source = normalizeReferenceReviewSnapshotPayload(snapshotOrContext);
    const metadata = asObject(source.snapshot_metadata);
    return {
        analysis_id: firstPresent(source.analysis_id, metadata.analysis_id) || null,
        document_version_id: firstPresent(
            source.document_version_id,
            source.contract_document_version_id,
            metadata.document_version_id,
            metadata.contract_document_version_id,
        ) || null,
        review_id: firstPresent(
            source.review_id,
            source.reference_review_id,
            metadata.review_id,
            metadata.reference_review_id,
        ) || null,
        snapshot_id: firstPresent(source.snapshot_id, metadata.snapshot_id) || null,
        stream_instance_id: firstPresent(
            source.stream_instance_id,
            metadata.stream_instance_id,
        ) || null,
    };
}

const snapshotFromCurrentRegister = (review) => {
    const requirementsById = asObject(review?.requirements_by_id);
    const verdictsById = asObject(review?.verdicts_by_id);
    const requirementOrder = Array.isArray(review?.requirement_order)
        ? review.requirement_order
        : Object.keys(requirementsById);
    const verdictOrder = Array.isArray(review?.verdict_order)
        ? review.verdict_order
        : Object.keys(verdictsById);
    const terminal = asObject(review?.terminal);
    const context = asObject(review?.context);
    return {
        ...terminal,
        ...context,
        requirements: requirementOrder.map((id) => requirementsById[id]).filter(Boolean),
        verdicts: verdictOrder.map((id) => verdictsById[id]).filter(Boolean),
        completion_status: firstPresent(terminal.completion_status, review?.status) || null,
        snapshot_persisted: terminal.snapshot_persisted === true,
    };
};

/**
 * During a run the progressively validated register wins. Before the first
 * verdict (or after an empty failed run), the last persisted snapshot remains
 * displayable. Requirements without a verdict are deliberately not cards.
 */
export function getDisplayableReferenceReviewSnapshot(review = {}) {
    const current = snapshotFromCurrentRegister(review);
    const failedTerminal = review?.status === 'failed'
        || current.completion_status === 'failed'
        || current.outcome_code === 'failed';
    if (failedTerminal) {
        if (review?.persisted_snapshot) {
            return {
                snapshot: normalizeReferenceReviewSnapshotPayload(review.persisted_snapshot),
                source: 'persisted',
            };
        }
        return {
            snapshot: {...current, requirements: [], verdicts: []},
            source: 'none',
        };
    }
    if (current.verdicts.length > 0) {
        return {snapshot: current, source: review?.result_source || 'stream'};
    }
    if (review?.persisted_snapshot) {
        return {
            snapshot: normalizeReferenceReviewSnapshotPayload(review.persisted_snapshot),
            source: 'persisted',
        };
    }
    return {snapshot: current, source: review?.result_source || 'none'};
}

const resolveSourceOrder = (requirement, fallbackIndex) => {
    const sourceOrder = Number(requirement?.source_order);
    return Number.isFinite(sourceOrder) ? sourceOrder : fallbackIndex;
};

const cardIdentity = (snapshot, review, requirementId) => {
    const metadata = asObject(snapshot.snapshot_metadata);
    const context = asObject(review?.context);
    const reviewId = firstPresent(
        snapshot.review_id,
        snapshot.reference_review_id,
        metadata.review_id,
        metadata.reference_review_id,
        context.review_id,
    );
    const fallbackScope = [
        firstPresent(snapshot.analysis_id, metadata.analysis_id, context.analysis_id, 'analysis'),
        firstPresent(
            snapshot.document_version_id,
            snapshot.contract_document_version_id,
            metadata.document_version_id,
            context.document_version_id,
            'document',
        ),
    ].join(':');
    return `reference_review:${reviewId || fallbackScope}:${requirementId}`;
};

/**
 * Pure projection from the reference-review register/snapshot to the common
 * visual-card contract. It does not write to ia_comments and preserves the
 * analytical statuses instead of inventing risk levels.
 */
export function projectReferenceReviewCards(referenceReview = {}) {
    const {snapshot: rawSnapshot, source} = getDisplayableReferenceReviewSnapshot(referenceReview);
    const snapshot = normalizeReferenceReviewSnapshotPayload(rawSnapshot);
    const metadata = asObject(snapshot.snapshot_metadata);
    const context = asObject(referenceReview?.context);
    const requirements = snapshot.requirements || [];
    const verdicts = snapshot.verdicts || [];
    const requirementsById = Object.fromEntries(
        requirements
            .filter((item) => item?.requirement_id)
            .map((item) => [String(item.requirement_id), item])
    );
    const verdictsById = Object.fromEntries(
        verdicts
            .filter((item) => item?.requirement_id)
            .map((item) => [String(item.requirement_id), item])
    );
    const requirementOrder = uniqueIds(requirements);
    const verdictOrder = uniqueIds(verdicts);
    const orderedIds = [
        ...requirementOrder.filter((id) => verdictsById[id]),
        ...verdictOrder.filter((id) => !requirementOrder.includes(id)),
    ];
    const criterionId = firstPresent(
        snapshot.criterion_id,
        metadata.criterion_id,
        metadata.criterion?.id,
        context.criterion_id,
    ) || null;
    const snapshotId = firstPresent(
        snapshot.snapshot_id,
        metadata.snapshot_id,
        context.snapshot_id,
    ) || null;
    const criterionName = firstPresent(
        snapshot.criterion_name,
        snapshot.criterion_title,
        metadata.criterion_name,
        metadata.criterion_title,
        metadata.criterion?.title,
        metadata.criterion?.name,
        context.criterion_name,
        REFERENCE_REVIEW_CRITERION_NAME,
    );
    const reviewId = firstPresent(
        snapshot.review_id,
        snapshot.reference_review_id,
        metadata.review_id,
        metadata.reference_review_id,
        context.review_id,
    ) || null;
    const referenceDocument = {
        ...asObject(metadata.reference_document),
        ...asObject(snapshot.reference_document),
    };
    const referenceDocumentId = firstPresent(
        snapshot.reference_document_id,
        snapshot.reference_file_id,
        metadata.reference_document_id,
        metadata.reference_file_id,
        referenceDocument.file_id,
        referenceDocument.id,
        context.reference_document_id,
    ) || null;
    const referenceDocumentTitle = firstPresent(
        snapshot.reference_document_title,
        snapshot.reference_file_title,
        metadata.reference_document_title,
        metadata.reference_file_title,
        referenceDocument.title,
        referenceDocument.name,
        context.reference_document_title,
    ) || null;
    const completionStatus = firstPresent(snapshot.completion_status, referenceReview?.status) || null;
    const snapshotPersisted = source === 'persisted' || snapshot.snapshot_persisted === true;

    return orderedIds
        .map((requirementId, fallbackIndex) => {
            const requirement = requirementsById[requirementId] || {};
            const verdict = verdictsById[requirementId];
            if (!verdict) return null;
            const sourceOrder = resolveSourceOrder(requirement, fallbackIndex);
            const searchText = [
                requirementId,
                requirement.title,
                verdict.summary,
                verdict.gap,
                verdict.recommendation,
            ].filter(Boolean).join(' ').toLocaleLowerCase();
            return {
                id: cardIdentity(snapshot, referenceReview, requirementId),
                criterion_id: criterionId,
                criterion_name: criterionName,
                criterion_title: criterionName,
                criterion: {
                    id: criterionId,
                    title: criterionName,
                    criterion_type: REFERENCE_REVIEW_SOURCE_TYPE,
                },
                source_type: REFERENCE_REVIEW_SOURCE_TYPE,
                reference_document_id: referenceDocumentId,
                reference_document_title: referenceDocumentTitle,
                review_id: reviewId,
                snapshot_id: snapshotId,
                requirement_id: requirementId,
                source_order: sourceOrder,
                importance: requirement.importance || null,
                status: verdict.status || null,
                title: requirement.title || verdict.title || requirementId,
                summary: verdict.summary || '',
                expected_position: requirement.expected_position || '',
                gap: verdict.gap || '',
                business_impact: verdict.business_impact || '',
                recommendation: verdict.recommendation || '',
                reference_evidence: Array.isArray(requirement.reference_evidence)
                    ? requirement.reference_evidence
                    : [],
                contract_evidence: Array.isArray(verdict.contract_evidence)
                    ? verdict.contract_evidence
                    : [],
                confidence: verdict.confidence || null,
                completion_status: completionStatus,
                coverage_complete: snapshot.coverage_complete === true,
                snapshot_persisted: snapshotPersisted,
                is_provisional: !snapshotPersisted,
                search_text: searchText,
                risk: null,
            };
        })
        .filter(Boolean)
        .sort((left, right) => (
            left.source_order - right.source_order
            || String(left.requirement_id).localeCompare(String(right.requirement_id), undefined, {numeric: true})
        ));
}
